import { useContext } from 'react';
import { Modal, ModalBody } from 'reactstrap';

import CloseIcon from '@paradigm/design-system/src/assets/CloseIcon';

import SelectField from '@paradigm/design-system/src/components/Select';
import { useDefaultAccountsActions } from '@paradigm/features/src/preferences/default-accounts/context';
import { isValidVenue } from '@paradigm/features/src/preferences/default-accounts/types';
import { exceptionLogger } from '@paradigm/logging/src/logging';
import { updateDefaultAccount } from '@paradigm/api/src/accounts';
import { useDefaultAccountsFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import DefaultAccountIndicator from './components/DefaultAccountIndicator';
import CustomAccountOption from './components/CustomAccountOption';
import Label from './components/Label';

import {
  DefaultAccountModalStateCtx,
  DefaultAccountModalActionsCtx,
} from './AccountSelectionProvider';

import type { AccountOption } from './types';

interface Props {
  venue: string;
  value: AccountOption | null | undefined;
  options: readonly AccountOption[] | undefined;
  placeholder: string | null;
  onChange: (value: AccountOption | null) => void;
  onSave: (value: AccountOption | null) => void;
}

export default function DefaultAccountModal(props: Props) {
  const { options, placeholder, value, venue, onChange, onSave } = props;

  const { setAllProductsDefault } = useDefaultAccountsActions();
  const isDefaultAccountEnabled = useDefaultAccountsFeatureFlag();
  const modalState = useContext(DefaultAccountModalStateCtx);
  const modalActions = useContext(DefaultAccountModalActionsCtx);

  const isDefault = value?.isDefault ?? false;

  const selectComponents = {
    Option: CustomAccountOption,
    ...(isDefault
      ? { IndicatorsContainer: DefaultAccountIndicator }
      : undefined),
  };

  const save = () => {
    if (value == null) return;
    const { id: accountId, value: accountName } = value;
    updateDefaultAccount({ venue, id: accountId })
      .then(() => {
        onSave(value);
        modalActions.close();
        if (isValidVenue(venue) && isDefaultAccountEnabled) {
          setAllProductsDefault(venue, accountName);
        }
      })
      .catch(exceptionLogger('Error updating default account'));
  };

  return (
    <Modal
      isOpen={modalState.isOpen}
      toggle={modalActions.close}
      keyboard
      fade={false}
      className="custom-dialog settings-modal"
    >
      <div className="d-flex justify-content-between align-items-center pl-5 pr-5 pt-2">
        <h5>Edit Default Account</h5>
        <CloseIcon
          width={15}
          height={15}
          className="close-icon"
          onClick={modalActions.close}
        />
      </div>
      <ModalBody className="pl-5 pr-5">
        <Label htmlFor="select-account">Default Account</Label>
        <SelectField
          id="select-account"
          options={options}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          isLoading={options == null}
          isDisabled={options == null}
          components={selectComponents}
        />
        <div className="text-right">
          <button
            onClick={save}
            className="btn primary-button mt-4"
            disabled={value == null}
          >
            Save
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
}
